import { notify } from '@billy/design-system'
import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { NotificationKeys } from '../../../enums/notificationKeys'
import { parsePayNowOAuthCallbackParams } from '../utils/parsePayNowOAuthCallback'
import {
  clearPayNowOAuthQueryParams,
  getPayNowOAuthCallbackSearch,
  validateAndConsumePayNowOAuthCsrfToken,
} from '../utils/payNowOAuth'
import { useConnectPayNow } from './useConnectPayNow'
import { useTrackCardPayment } from './useTrackCardPayment'

export const usePayNowOAuthCallback = () => {
  const { t } = useTranslation()
  const { trackStripeAccountConnected } = useTrackCardPayment()
  // Guards against StrictMode's double-invoke and remounts firing the callback (and the POST) twice.
  const hasHandledCallbackRef = useRef(false)

  // `isConnecting` stays true until the mutation's async onSuccess (which awaits both cache
  // invalidations) resolves, so it covers the whole exchange-plus-refetch window — no extra state.
  const { connectAsync, isConnecting } = useConnectPayNow({
    onError: () => {
      notify({
        id: NotificationKeys.PayNowConnectError,
        message: t('settings.organization.card_payments.connect.error_toast'),
        variant: 'error',
      })
    },
    onSuccess: () => {
      trackStripeAccountConnected()
      notify({
        id: NotificationKeys.PayNowConnectSuccess,
        message: t('settings.organization.card_payments.connect.success_toast'),
        variant: 'success',
      })
    },
  })

  useEffect(() => {
    if (hasHandledCallbackRef.current) {
      return
    }

    const callbackSearch = getPayNowOAuthCallbackSearch()

    if (!callbackSearch) {
      return
    }

    const { authorizationFailure, authorizationSuccess } = parsePayNowOAuthCallbackParams(callbackSearch)

    if (!authorizationFailure && !authorizationSuccess) {
      return
    }

    hasHandledCallbackRef.current = true
    clearPayNowOAuthQueryParams()

    // Consume the stored token on every path (called exactly once) so it never lingers in
    // sessionStorage — even on a pure failure where `state` is undefined and this returns false.
    const isCsrfValid = validateAndConsumePayNowOAuthCsrfToken(authorizationSuccess?.state)

    // Stripe denial (`?error=access_denied`) or a failed CSRF check both surface the same error toast.
    if (authorizationFailure || !authorizationSuccess || !isCsrfValid) {
      notify({
        id: NotificationKeys.PayNowConnectError,
        message: t('settings.organization.card_payments.connect.error_toast'),
        variant: 'error',
      })

      return
    }

    connectAsync(authorizationSuccess.code).catch(() => {
      // Failures already surface via the mutation's onError toast; swallow the rejection so it does
      // not bubble up as an unhandled promise rejection.
    })
  }, [connectAsync, t])

  return useMemo(() => ({ isProcessingOAuthCallback: isConnecting }), [isConnecting])
}
